import React, { useEffect, useState } from "react";
import { Button, Form, Table, Alert, Card } from "react-bootstrap";
import axios from "axios";
import Layout from '../../components/layouts/Main';
import config from '../../config';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from "react-router-dom";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";

export default function EditPackagingMaterialPO() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [orderData, setOrderData] = useState({
    supplier: "",
    orderDate: "",
    expectedDeliveryDate: "",
    paymentTerms: "",
    shippingCharges: 0,
    remarks: "",
    deliveryFrom: "",
    deliveryTo: "",
    status: "Draft"
  });

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [packingMaterials, setPackingMaterials] = useState([]);

  // Permission checks
  const canEditPO = useHasPermission(PERMISSIONS.PM_PO_EDIT);
  const canViewVendors = useHasPermission(PERMISSIONS.VIEW_VENDORS);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [];

        if (canViewVendors) {
          promises.push(axios.get(`${config.API_BASE_URL}/vendors?approved=true&status=Active`));
        }

        promises.push(axios.get(`${config.API_BASE_URL}/packing-materials`));
        promises.push(axios.get(`${config.API_BASE_URL}/pm-purchase-orders/${id}`));

        const responses = await Promise.all(promises);

        let responseIndex = 0;
        if (canViewVendors) {
          setSuppliers(responses[responseIndex].data);
          responseIndex++;
        }

        setPackingMaterials(responses[responseIndex].data);
        responseIndex++;

        const orderResponse = responses[responseIndex].data;
        const { pmPO } = orderResponse;

        // Set order data
        setOrderData({
          supplier: pmPO.supplier?._id || "",
          orderDate: pmPO.orderDate ? new Date(pmPO.orderDate).toISOString().split("T")[0] : "",
          expectedDeliveryDate: pmPO.expectedDeliveryDate ? new Date(pmPO.expectedDeliveryDate).toISOString().split("T")[0] : "",
          deliveryFrom: pmPO.deliveryFrom || "",
          deliveryTo: pmPO.deliveryTo || "",
          paymentTerms: pmPO.paymentTerms || "Credit 30",
          shippingCharges: pmPO.shippingCharges || 0,
          remarks: pmPO.remarks || "",
          status: pmPO.status || "Draft"
        });

        // Set items
        setItems(pmPO.items?.map(item => ({
          _id: item._id,
          packingMaterial: item.packingMaterial?._id || "",
          quantity: item.quantity || 0,
          unit: item.unit || "pcs",
          unitPrice: item.unitPrice || 0,
          taxRate: item.taxRate || 0,
          totalAmount: item.totalAmount || 0
        })) || []);

      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to load data");
      } finally {
        setFetchLoading(false);
      }
    };

    fetchData();
  }, [id, canViewVendors]);

  const handleOrderChange = (e) => {
    setOrderData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!canEditPO) {
      toast.error("You don't have permission to edit packaging material purchase orders");
      return;
    }

    setLoading(true);
    try {
      // Calculate totals
      let subtotal = 0;
      let totalTaxAmount = 0;

      const processedItems = items.map(item => {
        const baseAmount = item.quantity * item.unitPrice;
        const taxAmount = (baseAmount * item.taxRate) / 100;
        const totalAmount = baseAmount + taxAmount;

        subtotal += baseAmount;
        totalTaxAmount += taxAmount;

        return {
          packingMaterial: item.packingMaterial,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
          taxAmount: taxAmount,
          totalAmount: totalAmount,
          status: item.status || "Pending"
        };
      });

      const totalAmount = subtotal + totalTaxAmount + parseFloat(orderData.shippingCharges || 0);

      const orderPayload = {
        ...orderData,
        items: processedItems,
        subtotal: subtotal,
        taxAmount: totalTaxAmount,
        totalAmount: totalAmount
      };

      await axios.put(`${config.API_BASE_URL}/pm-purchase-orders/${id}`, orderPayload);

      toast.success("Packaging Material Purchase Order updated successfully");
      navigate(`/packaging-material-po/${id}`, {
        state: { toast: "Packaging Material Purchase Order updated successfully" }
      });
    } catch (err) {
      console.error("Error updating packaging material PO", err);
      toast.error("Error updating purchase order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // If user can't edit PO, show permission denied
  if (!canEditPO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/packaging-material-purchase-orders">Packaging Material POs</Link></li>
            <li className="breadcrumb-item active">Edit</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to edit packaging material purchase orders. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  if (fetchLoading) {
    return (
      <Layout>
        <div className="text-center py-4">
          <span className="spinner-border spinner-border-lg" role="status" aria-hidden="true"></span>
          <p className="mt-2">Loading order data...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/packaging-material-purchase-orders">Packaging Material POs</Link></li>
          <li className="breadcrumb-item"><Link to={`/packaging-material-po/${id}`}>PM PO Details</Link></li>
          <li className="breadcrumb-item active">Edit</li>
        </ol>
      </nav>

      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-1">Edit Packaging Material PO</h2>
          </div>

          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>Order Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <div className="row">
                  <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Supplier</Form.Label>
                    <Form.Select
                      name="supplier"
                      value={orderData.supplier}
                      onChange={handleOrderChange}
                      disabled={!canViewVendors}
                    >
                      <option value="">Select Supplier...</option>
                      {suppliers.map(v => (
                        <option key={v._id} value={v._id}>{v.business_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Order Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="orderDate"
                      value={orderData.orderDate}
                      onChange={handleOrderChange}
                    />
                  </Form.Group>

                  <Form.Group className="col-md-4 mb-3">
                    <Form.Label>Expected Delivery Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="expectedDeliveryDate"
                      value={orderData.expectedDeliveryDate}
                      onChange={handleOrderChange}
                    />
                  </Form.Group>
                </div>

                <div className="row">
                  <Form.Group className="col-md-3 mb-3">
                    <Form.Label>Payment Terms</Form.Label>
                    <Form.Select
                      name="paymentTerms"
                      value={orderData.paymentTerms}
                      onChange={handleOrderChange}
                    >
                      <option value="Advance">Advance</option>
                      <option value="COD">COD</option>
                      <option value="Credit 7">Credit 7 days</option>
                      <option value="Credit 15">Credit 15 days</option>
                      <option value="Credit 30">Credit 30 days</option>
                      <option value="Credit 45">Credit 45 days</option>
                      <option value="Credit 60">Credit 60 days</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="col-md-3 mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={orderData.status}
                      onChange={handleOrderChange}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Sent">Sent</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Partial">Partial</option>
                      <option value="Received">Received</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Closed">Closed</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="col-md-3 mb-3">
                    <Form.Label>Shipping Charges (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      name="shippingCharges"
                      value={orderData.shippingCharges}
                      onChange={handleOrderChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </Form.Group>

                  <Form.Group className="col-md-3 mb-3">
                    <Form.Label>Total Amount</Form.Label>
                    <Form.Control
                      type="text"
                      value={`₹${(() => {
                        const subtotal = items.reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0);
                        const taxAmount = items.reduce((sum, it) => (it.quantity * it.unitPrice * it.taxRate) / 100, 0);
                        const shipping = parseFloat(orderData.shippingCharges || 0);
                        return (subtotal + taxAmount + shipping).toFixed(2);
                      })()}`}
                      readOnly
                      className="bg-light fw-bold text-success"
                    />
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="remarks"
                    value={orderData.remarks}
                    onChange={handleOrderChange}
                    placeholder="Additional notes or instructions"
                  />
                </Form.Group>

                {items.length > 0 && (
                  <>
                    <hr />
                    <h6>Items in this Order:</h6>
                    <div className="table-responsive">
                      <Table bordered hover size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Packing Material</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Tax (%)</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it) => {
                            const packingMaterial = packingMaterials.find(pm => pm._id === it.packingMaterial);
                            return (
                              <tr key={it._id}>
                                <td>
                                  <div>
                                    <strong>{packingMaterial?.name || 'N/A'}</strong>
                                    {packingMaterial?.alias && <div className="small text-muted">({packingMaterial.alias})</div>}
                                  </div>
                                </td>
                                <td>{it.quantity} {it.unit}</td>
                                <td>₹{parseFloat(it.unitPrice).toFixed(2)}</td>
                                <td>{it.taxRate}%</td>
                                <td>₹{it.totalAmount?.toFixed(2) || "0.00"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    <Alert variant="warning">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Note:</strong> Item editing functionality is simplified in this edit view.
                      For detailed item modifications, please contact your administrator.
                    </Alert>
                  </>
                )}
              </Form>
            </Card.Body>
            <Card.Footer>
              <div className="d-flex justify-content-between">
                <Link to={`/packaging-material-po/${id}`} className="btn btn-secondary">
                  <i className="fas fa-arrow-left me-2"></i>Back
                </Link>
                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>Update PM Purchase Order
                    </>
                  )}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
